import { useState, useEffect, useCallback } from 'react';
import { getPlatformAnalytics } from '../api/platformAnalytics';
import type { PlatformAnalyticsData, PlatformPeriod, CrossTabEntry, DailyTrendEntry } from '../types/platformAnalytics';
import styles from './PlatformAnalytics.module.css';

const PLATFORM_COLORS: Record<string, string> = {
  web: '#3b82f6',
  mobile: '#22c55e',
  desktop: '#a855f7',
};

const OS_COLORS: Record<string, string> = {
  ios: '#a855f7',
  android: '#22c55e',
  macos: '#c084fc',
  windows: '#3b82f6',
  linux: '#f59e0b',
};

const OS_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  macos: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
};

const DEVICE_TYPE_COLORS: Record<string, string> = {
  desktop: '#3b82f6',
  phone: '#f59e0b',
  tablet: '#ec4899',
};

function pctBar(pct: number, color?: string) {
  return (
    <div className={styles.pctBarTrack}>
      <div
        className={styles.pctBarFill}
        style={{ width: `${Math.min(pct, 100)}%`, background: color }}
      />
    </div>
  );
}

function formatTrendDate(date: string): string {
  if (!date) return '-';
  const d = new Date(date.includes('T') ? date : date + 'T00:00:00');
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function PlatformAnalytics() {
  const [period, setPeriod] = useState<PlatformPeriod>('30d');
  const [data, setData] = useState<PlatformAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getPlatformAnalytics(period));
    } catch {
      setError('Failed to load platform analytics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary;
  const totalUsers = summary?.total_unique_users ?? 0;
  const pct = (n: number) => totalUsers > 0 ? Math.round((n / totalUsers) * 1000) / 10 : 0;

  // Build daily trend rows grouped by date
  const trendsByDate: Record<string, { web: DailyTrendEntry | null; mobile: DailyTrendEntry | null; desktop: DailyTrendEntry | null }> = {};
  if (data?.daily_trends) {
    for (const entry of data.daily_trends) {
      if (!trendsByDate[entry.day]) trendsByDate[entry.day] = { web: null, mobile: null, desktop: null };
      const key = entry.platform as 'web' | 'mobile' | 'desktop';
      if (key in trendsByDate[entry.day]) trendsByDate[entry.day][key] = entry;
    }
  }
  const trendDates = Object.keys(trendsByDate).sort();

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Platform Analytics</h1>
          <p className={styles.subtitle}>Device and platform usage across web, mobile and the desktop app (signed-in users)</p>
        </div>
        <div className={styles.headerControls}>
          <select
            className={styles.periodSelect}
            value={period}
            onChange={e => setPeriod(e.target.value as PlatformPeriod)}
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="all">All Time</option>
          </select>
          <button className={styles.refreshButton} onClick={load} disabled={loading}>
            <span className={`${styles.refreshIcon} ${loading ? styles.spinning : ''}`}>↻</span>
            Refresh
          </button>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading && !data ? (
        <div className={styles.loadingSmall}>Loading...</div>
      ) : data && summary ? (
        <>
          {/* ===== SUMMARY CARDS ===== */}
          <div className={styles.statCards}>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Total Users</div>
              <div className={styles.statValue}>{totalUsers.toLocaleString()}</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Web Users</div>
              <div className={styles.statValue}>{(summary.by_platform.web ?? 0).toLocaleString()}</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Mobile Users</div>
              <div className={styles.statValue}>{(summary.by_platform.mobile ?? 0).toLocaleString()}</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Desktop App Users</div>
              <div className={styles.statValue}>{(summary.by_platform.desktop ?? 0).toLocaleString()}</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statLabel}>Both Platforms</div>
              <div className={styles.statValue}>{summary.web_and_mobile_users.toLocaleString()}</div>
            </div>
          </div>

          {/* ===== BREAKDOWNS ===== */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Breakdowns</h2>
            <div className={styles.breakdownGrid}>
              {/* Platform */}
              <div className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>By Platform</h3>
                {Object.entries(summary.by_platform).map(([key, count]) => (
                  <div key={key} className={styles.breakdownRow}>
                    <span className={styles.breakdownLabel}>
                      <span className={styles.dot} style={{ background: PLATFORM_COLORS[key] || '#6b7280' }} />
                      {key}
                    </span>
                    <span className={styles.breakdownCount}>{count.toLocaleString()}</span>
                    <span className={styles.breakdownPct}>{pct(count)}%</span>
                    {pctBar(pct(count), PLATFORM_COLORS[key])}
                  </div>
                ))}
              </div>

              {/* OS */}
              <div className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>By OS (Mobile + Desktop app)</h3>
                {Object.keys(summary.by_os).length === 0 ? (
                  <p className={styles.emptyNotice}>No mobile or desktop app data yet</p>
                ) : (
                  Object.entries(summary.by_os).map(([key, count]) => {
                    const appTotal = (summary.by_platform.mobile ?? 0) + (summary.by_platform.desktop ?? 0);
                    const osPct = appTotal > 0 ? Math.round((count / appTotal) * 1000) / 10 : 0;
                    return (
                      <div key={key} className={styles.breakdownRow}>
                        <span className={styles.breakdownLabel}>
                          <span className={styles.dot} style={{ background: OS_COLORS[key] || '#6b7280' }} />
                          {OS_LABELS[key] || key}
                        </span>
                        <span className={styles.breakdownCount}>{count.toLocaleString()}</span>
                        <span className={styles.breakdownPct}>{osPct}%</span>
                        {pctBar(osPct, OS_COLORS[key])}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Device Type */}
              <div className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>By Device Type</h3>
                {Object.entries(summary.by_device_type).map(([key, count]) => (
                  <div key={key} className={styles.breakdownRow}>
                    <span className={styles.breakdownLabel}>
                      <span className={styles.dot} style={{ background: DEVICE_TYPE_COLORS[key] || '#6b7280' }} />
                      {key}
                    </span>
                    <span className={styles.breakdownCount}>{count.toLocaleString()}</span>
                    <span className={styles.breakdownPct}>{pct(count)}%</span>
                    {pctBar(pct(count), DEVICE_TYPE_COLORS[key])}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== CROSS-TABS ===== */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Cross-Tab Breakdown</h2>
            <div className={styles.crossTabGrid}>
              <div className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>Platform x Device Type</h3>
                {data.cross_tabs.platform_x_device_type.length === 0 ? (
                  <p className={styles.emptyNotice}>No data yet</p>
                ) : (
                  data.cross_tabs.platform_x_device_type.map((row: CrossTabEntry, i: number) => (
                    <div key={i} className={styles.breakdownRow}>
                      <span className={styles.breakdownLabel}>
                        <span className={styles.dot} style={{ background: PLATFORM_COLORS[row.platform ?? ''] || '#6b7280' }} />
                        {row.platform} / {row.device_type}
                      </span>
                      <span className={styles.breakdownCount}>{row.users.toLocaleString()}</span>
                      <span className={styles.breakdownPct}>{pct(row.users)}%</span>
                      {pctBar(pct(row.users), PLATFORM_COLORS[row.platform ?? ''])}
                    </div>
                  ))
                )}
              </div>

              <div className={styles.breakdownCard}>
                <h3 className={styles.breakdownTitle}>OS x Device Type (Mobile + Desktop app)</h3>
                {data.cross_tabs.os_x_device_type.length === 0 ? (
                  <p className={styles.emptyNotice}>No mobile or desktop app data yet</p>
                ) : (
                  data.cross_tabs.os_x_device_type.map((row: CrossTabEntry, i: number) => (
                    <div key={i} className={styles.breakdownRow}>
                      <span className={styles.breakdownLabel}>
                        <span className={styles.dot} style={{ background: OS_COLORS[row.os ?? ''] || '#6b7280' }} />
                        {OS_LABELS[row.os ?? ''] || row.os} / {row.device_type}
                      </span>
                      <span className={styles.breakdownCount}>{row.users.toLocaleString()}</span>
                      <span className={styles.breakdownPct}>{pct(row.users)}%</span>
                      {pctBar(pct(row.users), OS_COLORS[row.os ?? ''])}
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          {/* ===== DAILY TRENDS ===== */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Daily Active Users by Platform</h2>
            {trendDates.length === 0 ? (
              <p className={styles.emptyNotice}>No trend data for the selected period.</p>
            ) : (
              <div className={styles.trendTableScroll}>
                <table className={styles.miniTable}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Web Users</th>
                      <th>Mobile Users</th>
                      <th>Desktop App Users</th>
                      <th>Web Requests</th>
                      <th>Mobile Requests</th>
                      <th>Desktop Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trendDates.map(date => {
                      const row = trendsByDate[date];
                      return (
                        <tr key={date}>
                          <td className={styles.trendDate}>{formatTrendDate(date)}</td>
                          <td className={styles.webColor}>{row.web?.users ?? 0}</td>
                          <td className={styles.mobileColor}>{row.mobile?.users ?? 0}</td>
                          <td className={styles.desktopColor}>{row.desktop?.users ?? 0}</td>
                          <td>{row.web?.requests?.toLocaleString() ?? 0}</td>
                          <td>{row.mobile?.requests?.toLocaleString() ?? 0}</td>
                          <td>{row.desktop?.requests?.toLocaleString() ?? 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : !loading && (
        <div className={styles.emptyNotice}>No platform data available yet. Data will appear once users start accessing the app with updated clients.</div>
      )}
    </div>
  );
}
